import React from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { shortName } from '../../utils/ipoSubscription';
import { debutMood, formatCroresRaised, formatReturn, isMultibagger, returnTone } from '../../utils/ipoPerformance';

const VALUE_CLASS = 'mt-2 text-2xl sm:text-[28px] leading-tight font-extrabold tracking-tight truncate';
const NOTE_CLASS = 'mt-1.5 text-[11px] font-semibold truncate';

const LISTING_TONES = { gain: 'text-emerald-600', loss: 'text-rose-600', flat: 'text-slate-900' };
const RETURN_TONES = { gain: 'text-[#008080]', loss: 'text-rose-600', flat: 'text-slate-900' };

const StatCard = ({ label, className = '', children }) => (
  <li className={`min-w-0 bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5 ${className}`}>
    <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-500 truncate">{label}</p>
    {children}
  </li>
);

export const PerformanceStatCards = ({ stats, year }) => {
  const { total, raisedCr, avgListingGain, avgCurrentReturn, top, gainers, discounts, winRatio } = stats;
  const listingTone = avgListingGain === null ? 'flat' : returnTone(avgListingGain);
  const MoodIcon = listingTone === 'loss' ? ArrowDownRight : ArrowUpRight;

  return (
    <ul className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
      <StatCard label={`Total Issues (${year})`}>
        <p className={`${VALUE_CLASS} text-slate-900`}>{total}</p>
        <p className={`${NOTE_CLASS} text-emerald-600`}>{formatCroresRaised(raisedCr)} raised</p>
      </StatCard>

      <StatCard label="Avg Listing Gain">
        <p className={`${VALUE_CLASS} ${LISTING_TONES[listingTone]}`}>
          {avgListingGain === null ? '—' : formatReturn(avgListingGain)}
        </p>
        <p className={`${NOTE_CLASS} flex items-center gap-1 ${listingTone === 'loss' ? 'text-rose-600' : 'text-emerald-600'}`}>
          {avgListingGain !== null && <MoodIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
          <span className="truncate">{debutMood(avgListingGain)}</span>
        </p>
      </StatCard>

      <StatCard label="Avg Current Return">
        <p className={`${VALUE_CLASS} ${RETURN_TONES[avgCurrentReturn === null ? 'flat' : returnTone(avgCurrentReturn)]}`}>
          {avgCurrentReturn === null ? '—' : formatReturn(avgCurrentReturn)}
        </p>
        <p className={`${NOTE_CLASS} text-slate-500`}>vs Issue Price</p>
      </StatCard>

      <StatCard label={top && isMultibagger(top) ? 'Top Multibagger' : 'Top Performer'}>
        <p className="mt-2.5 text-base sm:text-lg leading-snug font-extrabold tracking-tight text-slate-900 line-clamp-2 break-words" title={top?.company}>
          {top ? shortName(top.company) : '—'}
        </p>
        <p className={`${NOTE_CLASS} mt-2 ${top && returnTone(top.currentReturn) === 'loss' ? 'text-rose-600' : 'text-emerald-600'}`}>
          {top ? `${formatReturn(top.currentReturn)} return` : 'No listings yet'}
        </p>
      </StatCard>

      <StatCard label="Listing Win Ratio" className="col-span-2 lg:col-span-1">
        <p className={`${VALUE_CLASS} text-slate-900`}>{winRatio === null ? '—' : `${winRatio.toFixed(1)}%`}</p>
        <div
          role="img"
          aria-label={`${gainers} of ${total} issues listed above the issue price and ${discounts} below it`}
          className="mt-2.5 flex h-1.5 rounded-full overflow-hidden bg-slate-100"
        >
          {total > 0 && (
            <>
              <span className="bg-emerald-500" style={{ width: `${(gainers / total) * 100}%` }} />
              <span className="bg-rose-500" style={{ width: `${(discounts / total) * 100}%` }} />
            </>
          )}
        </div>
        <p className="mt-1.5 text-[10px] font-medium text-slate-400 truncate">
          {gainers} Gainers • {discounts} Discount
        </p>
      </StatCard>
    </ul>
  );
};

export default PerformanceStatCards;
